import React, { useState } from "react";
import { Link } from "react-router-dom";
import background from "../../assets/images/sponsorbg1.png";
import headerImage from "../../assets/images/sponsorheader2.png";
import useScrollAnimation from "../../hooks/useScrollAnimation";
import SponsorWays from "./SponsorWays";

const ArrowIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

export default function SponsorHeader() {
  const [imgLoaded, setImgLoaded] = useState(false);
  const howRef = useScrollAnimation();

  const scrollToHowItWorks = () =>
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      {/* Hero — same structure as the homepage hero (ArdrendHero) */}
      <section className="relative h-screen min-h-[600px] flex items-center overflow-hidden bg-[#0b0f19]">
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#0b0f19] via-[#1a2333] to-[#05070c]" />
        <div className={`absolute inset-0 z-[1] transition-opacity duration-700 ${imgLoaded ? "opacity-100" : "opacity-0"}`}>
          <img
            src={background}
            alt=""
            role="presentation"
            className="w-full h-full object-cover object-center scale-105"
            fetchpriority="high"
            loading="eager"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
          />
        </div>
        <div className="absolute inset-0 z-10 w-full md:w-[75%] bg-gradient-to-r from-[#0b0f19]/95 via-[#0b0f19]/80 to-transparent" />

        <div className="relative z-20 w-full max-w-[1560px] mx-auto px-6 pt-16 md:pt-24">
          <div className="text-primary text-[12px] font-bold tracking-[0.25em] uppercase mb-5">
            Sponsor a learner
          </div>
          <h1 className="text-white text-4xl md:text-6xl lg:text-[72px] font-bold max-w-4xl leading-[1.05] tracking-tight mb-6 drop-shadow-lg">
            Fund a seat and<br />build a career<span className="text-primary">.</span>
          </h1>
          <p className="text-white/80 text-base md:text-xl max-w-2xl leading-relaxed mb-10">
            Thousands of young people have the drive to build careers in technology, but no clear
            pathway to get there. E1X Academy is that pathway, and your sponsorship helps a student
            who could not otherwise afford it.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={scrollToHowItWorks}
              className="bg-primary hover:bg-[#b0451e] text-white transition-colors rounded-full pl-5 pr-2 py-2 inline-flex items-center gap-3 group self-start shadow-md w-full sm:w-auto justify-between sm:justify-start"
            >
              <span className="text-[11px] font-bold uppercase tracking-wider">See how it works</span>
              <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-primary shrink-0">
                <ArrowIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </button>
            <Link
              to="/sponsor/participate"
              className="bg-transparent hover:bg-white/10 text-white rounded-full px-5 py-3 inline-flex items-center justify-center text-[11px] font-bold border border-white/30 transition-colors uppercase tracking-wider w-full sm:w-auto"
            >
              Become a sponsor
            </Link>
          </div>
        </div>
      </section>

      <SponsorWays />

      {/* How it works — copy unchanged, restyled to the new section format */}
      <section id="how-it-works" ref={howRef} className="py-24 bg-[#f7f7f7] scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 grid gap-12 lg:grid-cols-2 items-center">
          <div className="animate-on-scroll animate-fade-up">
            <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-6 flex items-center gap-3">
              <span className="block w-8 h-[2px] bg-primary" />
              The process
            </div>
            <h2 className="text-[#0b0f19] text-3xl md:text-5xl font-bold">
              How it works<span className="text-primary">.</span>
            </h2>
            <p className="mt-6 text-gray-500 text-lg leading-relaxed">
              Experience our 100% transparent contribution process. Customise your impact by
              creating a scholarship in your name or remaining anonymous. You dictate distribution
              criteria, choose recipients, and even have the option to meet the beneficiaries. It's
              all within your control.
            </p>
            <Link
              to="/sponsor/participate"
              className="mt-8 bg-primary hover:bg-[#b0451e] text-white transition-colors rounded-full pl-5 pr-2 py-2 inline-flex items-center gap-3 group shadow-md"
            >
              <span className="text-[11px] font-bold uppercase tracking-wider">Become a sponsor</span>
              <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-primary shrink-0">
                <ArrowIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          </div>
          <div className="animate-on-scroll animate-fade-up delay-200">
            <img
              src={headerImage}
              alt="A student working outdoors with a laptop"
              className="w-full h-[320px] md:h-[440px] object-cover rounded-tl-[100px] rounded-br-[100px]"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </>
  );
}
